// ─── Public Status Page — the systems tree ───────────────────────────
//
// Components NEST up to three levels (`Mæglerunivers › Annoncering ›
// Boligsiden`), and a branch is COLLAPSED behind a disclosure — the same shape
// the page this replaces used, and the reason a customer can take in thirty-odd
// services without scrolling past all of them.
//
// This reverses an earlier decision here ("a status page is scanned, not
// explored, so there are no collapsible sections"). The principle survives the
// reversal intact, because collapsing never hides a signal:
//
//   * a parent row carries its OWN rolled-up pill and its OWN rolled-up history
//     bar — worst-wins over its whole subtree, computed server-side in
//     public.ts — so a red child turns its ancestors red while still collapsed;
//   * anything not currently `available` is EXPANDED ON MOUNT, along with its
//     ancestors, so a live problem is on screen without a click;
//   * the disclosure is a real <button aria-expanded aria-controls>, so the tree
//     is keyboard- and screen-reader-operable rather than a CSS trick.
//
// What collapsing buys is that the healthy 95% of the page stays one line per
// top-level service.

import { useMemo, useState, type ReactNode } from 'react';
import type { PublicStatusComponent } from '@pcp/shared';
import type { ComponentTreeProps } from '../types';
import { ComponentRow } from './ComponentRow';

/**
 * Ids to expand on first paint: every node that is not `available`, plus every
 * ancestor on the way to it (expanding a leaf is useless if its parent is shut).
 *
 * Computed once per mount rather than derived on every render, so a visitor who
 * collapses a branch during an incident is not fighting the component to keep it
 * shut. A later poll that changes a status does not re-expand either — the page
 * re-renders constantly against the snapshot, and having rows spring open under
 * the cursor is worse than the extra click.
 */
function autoExpanded(nodes: PublicStatusComponent[]): Set<string> {
  const open = new Set<string>();
  const walk = (node: PublicStatusComponent, ancestors: string[]): void => {
    if (node.status !== 'available' && node.children.length > 0) open.add(node.id);
    if (node.status !== 'available') for (const a of ancestors) open.add(a);
    for (const child of node.children) walk(child, [...ancestors, node.id]);
  };
  for (const node of nodes) walk(node, []);
  return open;
}

export function ComponentTree({
  hrefForDay,
  onOpenDay,
  components,
  historyDays,
  historyStartDate,
  dayMinutes,
  onOpenComponent,
  hrefForComponent,
}: ComponentTreeProps) {
  const initial = useMemo(() => autoExpanded(components), [components]);
  const [expanded, setExpanded] = useState<Set<string>>(initial);

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** One node and, when open, its subtree. Recursive: depth is capped server-side. */
  const renderNode = (node: PublicStatusComponent): ReactNode => {
    const hasChildren = node.children.length > 0;
    const isOpen = hasChildren && expanded.has(node.id);
    const childrenId = `sp-children-${node.id}`;

    return (
      <div className="sp-node" key={node.id}>
        <ComponentRow
          component={node}
          hrefForDay={hrefForDay}
          onOpenDay={onOpenDay}
          historyDays={historyDays}
          historyStartDate={historyStartDate}
          dayMinutes={dayMinutes}
          hasChildren={hasChildren}
          expanded={isOpen}
          onToggle={() => toggle(node.id)}
          childrenId={childrenId}
          detailHref={hrefForComponent(node)}
          onOpenDetail={() => onOpenComponent(node)}
        />
        {hasChildren && (
          // Kept MOUNTED and hidden rather than unmounted: `hidden` keeps it out
          // of the accessibility tree and out of find-in-page the same way, and
          // it means expanding never re-runs the history bars' width measurement
          // from scratch.
          <div className="sp-node-children" id={childrenId} hidden={!isOpen}>
            {node.children.map(renderNode)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sp-groups">
      {components.map((root) => (
        <section className="sp-group" key={root.id}>
          {renderNode(root)}
        </section>
      ))}
    </div>
  );
}
